import { putPhoto } from '@jq4race/api-client';
import type { PhotoAngle, PhotoUploadUrlsResponse } from '@jq4race/shared';

import type { PhotoSink } from '../domain/ports/photo-sink.js';

/** Presigned PUT tokens expire at 15 min; re-issue slightly before that. */
const TARGET_AGE_LIMIT_MS = 14 * 60 * 1000;

/**
 * RemotePhotoSink — today's upload-at-capture behavior (extracted verbatim
 * from the pre-Slice-4 `usePhotoUpload`, unchanged for Flash): uploads
 * straight to R2 via a presigned PUT URL, caching the issued targets for up
 * to 14 minutes and transparently retrying once (with freshly re-issued
 * targets) on failure.
 */
export class RemotePhotoSink implements PhotoSink {
  private targets: PhotoUploadUrlsResponse['uploads'] | null = null;
  private issuedAt = 0;

  constructor(private readonly requestUrls: () => Promise<PhotoUploadUrlsResponse>) {}

  private async ensureTargets(): Promise<PhotoUploadUrlsResponse['uploads']> {
    const now = Date.now();
    if (this.targets && now - this.issuedAt < TARGET_AGE_LIMIT_MS) {
      return this.targets;
    }
    const response = await this.requestUrls();
    this.targets = response.uploads;
    this.issuedAt = Date.now();
    return this.targets;
  }

  async upload(angle: PhotoAngle, blob: Blob, isRetry = false): Promise<{ key: string }> {
    try {
      const targets = await this.ensureTargets();
      const target = targets.find((t) => t.angle === angle);
      if (!target) throw new Error(`No target for angle: ${angle}`);

      await putPhoto(target.url, blob);
      return { key: target.key };
    } catch (err) {
      if (!isRetry) {
        // Transparent single retry: re-request all targets and retry once.
        this.targets = null;
        return this.upload(angle, blob, true);
      }
      // Never swallowed — propagates to usePhotoUpload's catch, which
      // surfaces a slot error to the operator.
      throw err;
    }
  }

  reset(): void {
    this.targets = null;
  }
}
